import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { RecertFormData } from 'components/Assessments/RecertAssessment/types/RecertFormData'
import { RecertAssessmentQuestionnaireItem } from 'components/Assessments/RecertAssessment/types/questionnaire'
import { RecertFormField } from 'components/Assessments/RecertAssessment/types/RecertFormField'
import { getAnswerValuesFromItems } from 'components/Assessments/helpers'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { BaseSubsectionAdapter } from '../../../../../adapters/BaseSubsectionAdapter'

/**
 * Sensory subsection adapter
 * Handles sensory status for all five senses plus system summary (6 fields)
 */
export class SensoryAdapter extends BaseSubsectionAdapter<RecertFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<RecertFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // All sensory status fields
    const statusEyes = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_SENSORY_SENSORY_STATUS_EYES
    )?.at(0)

    const statusEars = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_SENSORY_SENSORY_STATUS_EARS
    )?.at(0)

    const statusNose = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_SENSORY_SENSORY_STATUS_NOSE
    )?.at(0)

    const statusTouch = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_SENSORY_SENSORY_STATUS_TOUCH
    )?.at(0)

    const statusTaste = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_SENSORY_SENSORY_STATUS_TASTE
    )?.at(0)

    const systemSummary = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_SENSORY_SYSTEM_SUMMARY
    )?.at(0)

    return {
      [RecertFormField.SENSORY_STATUS_EYES]: statusEyes,
      [RecertFormField.SENSORY_STATUS_EARS]: statusEars,
      [RecertFormField.SENSORY_STATUS_NOSE]: statusNose,
      [RecertFormField.SENSORY_STATUS_TOUCH]: statusTouch,
      [RecertFormField.SENSORY_STATUS_TASTE]: statusTaste,
      [RecertFormField.SENSORY_SYSTEM_SUMMARY]: systemSummary
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    if (this.formDataFieldExists(RecertFormField.SENSORY_STATUS_EYES)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_SENSORY_SENSORY_STATUS_EYES,
        value: this.getFormDataField(RecertFormField.SENSORY_STATUS_EYES)
      })
    }

    if (this.formDataFieldExists(RecertFormField.SENSORY_STATUS_EARS)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_SENSORY_SENSORY_STATUS_EARS,
        value: this.getFormDataField(RecertFormField.SENSORY_STATUS_EARS)
      })
    }

    if (this.formDataFieldExists(RecertFormField.SENSORY_STATUS_NOSE)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_SENSORY_SENSORY_STATUS_NOSE,
        value: this.getFormDataField(RecertFormField.SENSORY_STATUS_NOSE)
      })
    }

    if (this.formDataFieldExists(RecertFormField.SENSORY_STATUS_TOUCH)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_SENSORY_SENSORY_STATUS_TOUCH,
        value: this.getFormDataField(RecertFormField.SENSORY_STATUS_TOUCH)
      })
    }

    if (this.formDataFieldExists(RecertFormField.SENSORY_STATUS_TASTE)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_SENSORY_SENSORY_STATUS_TASTE,
        value: this.getFormDataField(RecertFormField.SENSORY_STATUS_TASTE)
      })
    }

    if (this.formDataFieldExists(RecertFormField.SENSORY_SYSTEM_SUMMARY)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_SENSORY_SYSTEM_SUMMARY,
        value: this.getFormDataField(RecertFormField.SENSORY_SYSTEM_SUMMARY)
      })
    }

    return mutations
  }
}
